import React, { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { connect } from "react-redux"
import style from "./DashboardFinal.module.scss"
import {
  clearOnboardingForms,
  clearProjects,
  closeMainModal,
  openTutorialModal,
  viewAccessRequest,
} from "../../store/actions"
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx"
import HeaderNavImproved from "../../components/Nav/HeaderNavImproved/HeaderNavImproved.tsx"
import DashboardHeader, { MobileResultsPayload } from "../../components/Dashboard/DashboardHeader/DashboardHeader.tsx"
import SuggestedPrompts from "../../components/Dashboard/SuggestedPrompts/SuggestedPrompts.tsx"
import AIAgentOrbLoader from "../../components/Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx"
import ProfilePostSuggestionModal from "../../components/UI/ProfilePostSuggestionModal/ProfilePostSuggestionModal.tsx"
import CommerceDashboardBanner from "../../components/Dashboard/CommerceDashboardBanner/CommerceDashboardBanner.tsx"
import useNavVisibility from "../../hooks/useNavVisibility.ts"
import BackgroundEffect from "../../components/Background/BackgroundEffect.tsx"
import { fireAnalytics } from "../../utils/firebase"
import {
  getSessionId,
  getDeviceCategory,
  getPreviousPage,
  getDashboardSource,
  checkFirstVisitToday,
  setDashboardViewTime,
  getTimeOnDashboard,
  getTabsViewedCount,
  getActionsClickedCount,
  clearDashboardTracking,
} from "../../utils/analyticsHelpers.ts"
import { shouldAutoShowWelcomeTour } from "../../hooks/useWelcomeTour.ts"

const DASHBOARD_CONVERSATIONAL_ROOT_CLASS = 'edify-dashboard-conversational-root'

const Container = styled.div<{ $conversational?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  padding-right: ${(p) => (p.$conversational ? '0' : '2%')};
  padding-left: ${(p) => (p.$conversational ? '0' : '2%')};
  margin: auto;
  background-color: transparent;

  @media (max-width: 1200px) {
    padding-right: ${(p) => (p.$conversational ? '0' : '2%')};
    padding-left: ${(p) => (p.$conversational ? '0' : '2%')};
  }

  @media (max-width: 900px) {
    padding-right: 0;
    padding-left: 0;
    min-height: 100dvh;
  }

  @media (max-width: 600px) {
    min-height: 100dvh;
  }
`

interface DashboardFinalProps {
  openTutorialModal: () => void
  clearProjects: () => void
  closeMainModal: () => void
  viewAccessRequest: () => void
  userData?: any
}

const DashboardFinal: React.FC<DashboardFinalProps> = ({
  openTutorialModal,
  userData,
  clearProjects,
  viewAccessRequest,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility()
  const [mobileResultsState, setMobileResultsState] = useState<MobileResultsPayload>({ status: 'idle' })
  const [isConversationalModeActive, setIsConversationalModeActive] = useState(false)
  const [agentStatus, setAgentStatus] = useState<'idle' | 'thinking' | 'responding' | 'error' | 'offline'>('idle')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isPostModalOpen, setIsPostModalOpen] = useState(false)
  type ConversationalActions = {
    onCreateNewSession: () => void
    onOpenSearchModal: () => void
    onCloseConversationalMode: () => void
    hasActiveConversation?: boolean
    totalChatsCount?: number
  }
  const [conversationalActions, setConversationalActions] = useState<ConversationalActions | null>(null)
  const dashboardStartTimeRef = useRef<number | null>(null)
  const pendingWelcomeTourAfterProfileModalRef = useRef(false)

  useEffect(() => {
    viewAccessRequest()
    clearOnboardingForms()
    clearProjects()
    
    setDashboardViewTime()
    dashboardStartTimeRef.current = Date.now()
    
    fireAnalytics('dashboard_view', {
      edify_user_id: userData?.id || '',
      session_id: getSessionId(),
      source: getDashboardSource(),
      previous_page: getPreviousPage(),
      is_first_visit_today: checkFirstVisitToday(),
      user_type: userData?.type || '',
      device_category: getDeviceCategory(),
      category: 'dashboard',
      action: 'view'
    })
    
    return () => {
      const timeSpent = dashboardStartTimeRef.current 
        ? Math.floor((Date.now() - dashboardStartTimeRef.current) / 1000)
        : getTimeOnDashboard()
      
      if (timeSpent > 0) {
        fireAnalytics('dashboard_engagement', {
          edify_user_id: userData?.id || '',
          session_id: getSessionId(),
          engagement_time_msec: timeSpent * 1000,
          tabs_viewed: getTabsViewedCount(),
          actions_clicked: getActionsClickedCount(),
          device_category: getDeviceCategory(),
          category: 'dashboard',
          action: 'engagement'
        })
      }
      
      clearDashboardTracking()
    }
  }, [userData, clearProjects, viewAccessRequest])

  useEffect(() => {
    if (!isConversationalModeActive) {
      setSidebarCollapsed(false)
    }
  }, [isConversationalModeActive])

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const cls = DASHBOARD_CONVERSATIONAL_ROOT_CLASS
    const onDashboard = location.pathname === '/dashboard'
    const apply = isConversationalModeActive && onDashboard
    if (apply) {
      html.classList.add(cls)
      body.classList.add(cls)
    } else {
      html.classList.remove(cls)
      body.classList.remove(cls)
    }
    return () => {
      html.classList.remove(cls)
      body.classList.remove(cls)
    }
  }, [isConversationalModeActive, location.pathname])

  useEffect(() => {
    const cls = DASHBOARD_CONVERSATIONAL_ROOT_CLASS
    return () => {
      document.documentElement.classList.remove(cls)
      document.body.classList.remove(cls)
    }
  }, [])

  useEffect(() => {
    const navState = location.state as { showProfilePostModal?: boolean } | null
    if (navState?.showProfilePostModal) {
      if (shouldAutoShowWelcomeTour()) {
        pendingWelcomeTourAfterProfileModalRef.current = true
      }
      setIsPostModalOpen(true)
      navigate(location.pathname, { replace: true, state: {} })
      return
    }
    if (pendingWelcomeTourAfterProfileModalRef.current) {
      return
    }
    if (shouldAutoShowWelcomeTour()) {
      openTutorialModal()
    }
  }, [location.state, location.pathname, navigate, openTutorialModal])

  return (
    <>
      <Container $conversational={isConversationalModeActive}>
        <BackgroundEffect />
        {!ocultarEnMobile && (
          <HeaderNavImproved
            isConversationalModeActive={isConversationalModeActive}
            onCreateNewSession={conversationalActions?.onCreateNewSession}
            onOpenSearchModal={conversationalActions?.onOpenSearchModal}
            onCloseConversationalMode={conversationalActions?.onCloseConversationalMode}
            hasActiveConversation={conversationalActions?.hasActiveConversation}
            totalChatsCount={conversationalActions?.totalChatsCount}
            isSidebarCollapsed={sidebarCollapsed}
          />
        )}
        <CommerceDashboardBanner userData={userData} conversationalActive={isConversationalModeActive} />
        {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
        <div
          className={[
            style.mainContainer,
            isConversationalModeActive ? style.mainContainerConversational : '',
            isConversationalModeActive && ocultarEnMobile ? style.mainContainerConversationalMobile : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {!isConversationalModeActive && <AIAgentOrbLoader status={agentStatus} />}
          <DashboardHeader 
            userName={
              userData?.type === "COMPANY" 
                ? userData?.razon_social 
                : userData?.name + " " + userData?.last_name || "Usuario"
            }
            userId={userData?.id}
            userData={userData}
            onMobileResultsChange={setMobileResultsState}
            onConversationalModeChange={setIsConversationalModeActive}
            onAgentStatusChange={(isProcessing) => {
              setAgentStatus(isProcessing ? 'thinking' : 'idle')
            }}
            onConversationalActionsReady={(actions) => {
              setConversationalActions(actions)
            }}
            onSidebarStateChange={setSidebarCollapsed}
            sidebarCollapsed={sidebarCollapsed}
            mobileActionsPlaceholder={
              ocultarEnMobile && !isConversationalModeActive ? (
                <div className={style.mobileActionsPlaceholder}>
                  <span>Contanos qué querés hacer y te ayudamos con la IA de Edify</span>
                </div>
              ) : null
            }
          />
          
          {!ocultarEnMobile && !isConversationalModeActive && (
            <div className={style.actionsSection}>
              <SuggestedPrompts userId={userData?.id} userData={userData} />
            </div>
          )}
        </div>
      </Container>
      <ProfilePostSuggestionModal
        isOpen={isPostModalOpen}
        onClose={() => {
          setIsPostModalOpen(false)
          if (pendingWelcomeTourAfterProfileModalRef.current) {
            pendingWelcomeTourAfterProfileModalRef.current = false
            if (shouldAutoShowWelcomeTour()) {
              openTutorialModal()
            }
          }
        }}
        userData={userData}
      />
    </>
  )
}

const mapStateToProps = (state: any) => ({
  userData: state.general?.userData,
})

export default connect(mapStateToProps, {
  clearProjects,
  closeMainModal,
  openTutorialModal,
  viewAccessRequest,
})(DashboardFinal)

